<template>
  <!-- The shell carries the inline-size container so that container queries can
       target the .listing-card element itself (a container can't query its own
       size to style itself, only its descendants). -->
  <div class="listing-card-shell">
  <article
    class="listing-card"
    :class="[
      `listing-card--${variant}`,
      { 'listing-card--sold-out': isSold },
    ]"
    :aria-label="ariaLabel"
  >
    <!-- Photo -->
    <a
      class="listing-card__media"
      :href="detailsHref"
      target="_blank"
      rel="noopener"
      :aria-label="estate?.name || $t('search_card.fr_noname')"
    >
      <img
        v-if="imageUrl"
        :src="imageUrl"
        :alt="estate?.name || ''"
        loading="lazy"
      >
      <span v-else class="listing-card__media-placeholder" aria-hidden="true">
        <base-icon name="base/MapPin" size="32px" color="var(--gray-monochrome-40)" />
      </span>

      <span v-if="isSold" class="listing-card__sold-pill">
        {{ $t("listing_card.fully_funded") }}
      </span>

      <span
        v-if="providerLogoUrl"
        class="listing-card__provider-chip"
        :title="$t('listing_card.via', { provider: providerName })"
        :style="providerChipStyle"
        role="img"
        :aria-label="providerName"
      />
    </a>

    <!-- Body -->
    <div class="listing-card__body">
      <a
        class="listing-card__title"
        :href="detailsHref"
        target="_blank"
        rel="noopener"
      >
        <span class="listing-card__name">{{
          estate?.name || $t("search_card.fr_noname")
        }}</span>
        <span class="listing-card__meta">
          <base-icon
            v-if="subtypeIcon"
            :name="`unit-subtypes/${subtypeIcon}`"
            size="14px"
            filled
          />
          <span>{{ estate?.location || $t("search_card.fr_loc") }}</span>
        </span>
      </a>

      <p
        v-if="variant === 'row' && descriptionPreview"
        class="listing-card__description"
      >
        {{ descriptionPreview }}
      </p>

      <div class="listing-card__metrics">
        <div class="listing-card__metric">
          <span class="listing-card__metric-label">
            <span>{{ $t("listing_card.label_min_ticket") }}</span>
            <base-tooltip :tooltip-text="$t('listing_card.tooltip_min_ticket')">
              <template #icon>
                <base-icon
                  name="base/Info"
                  size="12px"
                  color="var(--text-secondary)"
                />
              </template>
            </base-tooltip>
          </span>
          <span class="listing-card__metric-value">{{ minInvestmentDisplay }}</span>
        </div>
        <span class="listing-card__metric-sep" aria-hidden="true" />
        <div class="listing-card__metric">
          <span class="listing-card__metric-label">
            <span>{{ $t("listing_card.label_apr") }}</span>
            <base-tooltip :tooltip-text="$t('listing_card.tooltip_apr')">
              <template #icon>
                <base-icon
                  name="base/Info"
                  size="12px"
                  color="var(--text-secondary)"
                />
              </template>
            </base-tooltip>
          </span>
          <span class="listing-card__metric-value">{{ aprDisplay }}</span>
        </div>
      </div>

      <div class="listing-card__actions">
        <div v-if="fundedPercent !== null" class="listing-card__progress">
          <div class="listing-card__progress-header">
            <span class="listing-card__progress-pct">{{
              $t("listing_card.sold_pct", { pct: fundedPercent })
            }}</span>
            <span
              v-if="dollarsLeftDisplay && !isSold"
              class="listing-card__progress-left"
            >
              {{ $t("listing_card.left_to_invest", { amount: dollarsLeftDisplay }) }}
            </span>
          </div>
          <div class="listing-card__progress-track">
            <div
              class="listing-card__progress-fill"
              :style="{ width: `${fundedPercent}%` }"
            />
          </div>
        </div>

        <div v-if="holdersLine" class="listing-card__trust">
          <base-icon
            name="base/User"
            size="14px"
            color="var(--text-secondary)"
          />
          <span>{{ holdersLine }}</span>
        </div>

        <button
          type="button"
          class="listing-card__cta"
          :disabled="isSold || !hasAnyCtaTarget"
          :aria-disabled="isSold || !hasAnyCtaTarget"
          @click="onInvest"
        >
          <span>{{ ctaLabel }}</span>
        </button>

        <a
          v-if="estate?.sanitizeName && (isSold || hasExternalTarget)"
          :href="detailsHref"
          target="_blank"
          rel="noopener"
          class="listing-card__details"
        >
          {{ $t("listing_card.open_details_secondary") }}
        </a>
      </div>
    </div>
  </article>
  </div>
</template>

<script setup lang="ts">
import { gtagReachGoal } from "~/services/SEO/goals/createCustomGoal";
import { getThumbnail } from "~/utilities/helpers/thumbnails/getThumbnail";
import { redirectByProviderName } from "~/utilities/helpers/strings/redirectByProviderName";
import { buildReferralRedirectUrl } from "~/utilities/helpers/strings/buildReferralRedirectUrl";
import { BASE_CURRENCY, HUGE_CURRENCY } from "~/composables/CURRENCIES";
import type { UpdatedUnitResponseDTO } from "~/store/unitPage";

type PropertyType = "estates" | "lands";
type Variant = "row" | "tile";
type Source = "directory" | "homepage" | "suggestions" | "map" | "favorites" | "profile";

interface Props {
  estate: UpdatedUnitResponseDTO;
  variant?: Variant;
  propertyType?: PropertyType;
  source?: Source;
}

const props = withDefaults(defineProps<Props>(), {
  variant: "tile",
  propertyType: "estates",
  source: "directory",
});

const { t } = useI18n();
const localePath = useLocalePath();

// --- Derived data --------------------------------------------------------

const isSold = computed(() => props.estate?.status === "sold_out");

const providerName = computed(() => props.estate?.provider?.name || "");

const detailsRoute = computed(() =>
  localePath(`/property/${props.estate?.sanitizeName || ""}`)
);

const detailsHref = computed(() =>
  props.estate?.sanitizeName ? detailsRoute.value : undefined
);

const imageUrl = computed(() => {
  const files = props.estate?.files || [];
  const withThumbs = files.find((f) => (f?.file?.thumbnails?.length ?? 0) > 0);
  return getThumbnail(withThumbs?.file || files[0]?.file, "thumbnail_470x370");
});

const SUBTYPE_ICONS: Record<string, string> = {
  Apartment: "apartment",
  House: "house",
  Townhouse: "townhouse",
  Condo: "apartment",
  Studio: "apartment",
  Villa: "house",
  Other: "other",
};

const subtypeIcon = computed(() => {
  const subtype = props.estate?.subtype;
  if (subtype && SUBTYPE_ICONS[subtype]) return SUBTYPE_ICONS[subtype];
  return null;
});

// Description can arrive as HTML or Markdown; strip tags / decode the common
// entities so the card preview is clean plain text that the CSS line-clamp
// can shape into however many lines fit between the title and the metrics row.
const descriptionPreview = computed(() => {
  const raw = props.estate?.description || "";
  if (!raw) return "";
  return raw
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
});

// --- Numbers -------------------------------------------------------------

// Convert a USD value to the user's selected base currency. When the listing's
// native currency matches the base, prefer the native value to avoid rate drift.
const convertFromUSD = (
  usd: number | null,
  nativeValue?: number | string | null,
  nativeCurrency?: string | null
): number | null => {
  if (usd === null) return null;
  const base = BASE_CURRENCY.value;
  if (nativeCurrency && nativeCurrency === base.code) {
    const n = typeof nativeValue === "string" ? parseFloat(nativeValue) : nativeValue;
    if (typeof n === "number" && !Number.isNaN(n) && n > 0) return n;
  }
  return usd * base.rate;
};

const formatMoney = (
  usd: number | null,
  nativeValue?: number | string | null,
  nativeCurrency?: string | null
) => {
  const value = convertFromUSD(usd, nativeValue, nativeCurrency);
  if (value === null) return "—";
  const base = BASE_CURRENCY.value;
  // Currencies with very large nominal values (e.g. IDR) get abbreviated
  // earlier so they don't blow out the metric cell.
  if (HUGE_CURRENCY.includes(base.code) && value >= 10000) {
    return `${base.symbol}${formatMoneyShortInBase(value)}`;
  }
  if (value >= 100000) {
    return `${base.symbol}${Math.round(value).toLocaleString("en-US")}`;
  }
  return `${base.symbol}${value.toLocaleString("en-US", {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

const apr = computed<number | null>(() => {
  const v = props.estate?.apr;
  return typeof v === "number" && v > 0 ? v : null;
});

const tokenPriceUSD = computed<number | null>(() => {
  const raw = props.estate?.tokenomics?.priceUSD;
  if (raw === null || raw === undefined) return null;
  const value = typeof raw === "string" ? parseFloat(raw) : raw;
  return typeof value === "number" && !Number.isNaN(value) && value > 0
    ? value
    : null;
});

const minInvestment = computed<number | null>(() => {
  const v = props.estate?.minimumInvestmentUSD;
  return typeof v === "number" && v > 0 ? v : null;
});

const aprDisplay = computed(() =>
  apr.value !== null ? `${apr.value.toFixed(1)}%` : "—"
);
const minInvestmentDisplay = computed(() =>
  formatMoney(
    minInvestment.value,
    props.estate?.minimumInvestment,
    props.estate?.currency
  )
);

// Compact money format for amounts already converted into the base currency.
const formatMoneyShortInBase = (value: number) => {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  if (value >= 1000) {
    return `${Math.round(value / 1000)}K`;
  }
  return `${Math.round(value).toLocaleString("en-US")}`;
};

// Compact "$300 / $4.7K / $1.2M" headlines — converts a USD amount first.
const formatMoneyShort = (usd: number | null) => {
  const value = convertFromUSD(usd);
  if (value === null) return "";
  return `${BASE_CURRENCY.value.symbol}${formatMoneyShortInBase(value)}`;
};

// --- Supply --------------------------------------------------------------

const availableSupplyPct = computed<number | null>(() => {
  const v = props.estate?.tokenomics?.availableSupplyPercentage;
  return typeof v === "number" && v >= 0 && v <= 100 ? v : null;
});

const fundedPercent = computed<number | null>(() => {
  if (isSold.value) return 100;
  if (availableSupplyPct.value === null) return null;
  const funded = 100 - availableSupplyPct.value;
  return Math.max(0, Math.min(100, Math.round(funded * 100) / 100));
});

const tokensLeft = computed<number | null>(() => {
  const raw = props.estate?.tokenomics?.availableSupply;
  if (raw === null || raw === undefined) return null;
  const value = typeof raw === "string" ? parseInt(raw, 10) : raw;
  return typeof value === "number" && !Number.isNaN(value) && value > 0
    ? value
    : null;
});

const dollarsLeftDisplay = computed(() => {
  if (tokensLeft.value === null || tokenPriceUSD.value === null) return "";
  return formatMoneyShort(tokensLeft.value * tokenPriceUSD.value);
});

const holdersCount = computed<number | null>(() => {
  const holders = props.estate?.holders;
  if (!Array.isArray(holders) || holders.length === 0) return null;
  return holders.length;
});

const holdersLine = computed(() => {
  if (holdersCount.value === null) return "";
  return t("main.m_pick_holders", { n: holdersCount.value });
});

// --- Provider ------------------------------------------------------------

const providerLogoUrl = computed(() => props.estate?.provider?.logoFile?.url || "");

// Provider chip hugs the logo: the chip width is computed from the logo's true
// aspect ratio so the white background never extends past the artwork.
// Backend SVGs often declare width="100%" which would collapse a plain <img>,
// so we fall back to viewBox parsing when naturalWidth is zero/one.
const LOGO_HEIGHT_PX = 20;
const LOGO_HORIZONTAL_PADDING_PX = 10;
const logoAspectCache = new Map<string, number>();
const logoAspectRatio = ref<number | null>(null);

const providerChipStyle = computed(() => {
  const url = providerLogoUrl.value;
  if (!url) return undefined;
  const style: Record<string, string> = {
    backgroundImage: `url(${url})`,
  };
  if (logoAspectRatio.value && logoAspectRatio.value > 0) {
    const width = Math.round(
      logoAspectRatio.value * LOGO_HEIGHT_PX + LOGO_HORIZONTAL_PADDING_PX * 2
    );
    style.width = `${width}px`;
  }
  return style;
});

async function resolveLogoAspectRatio(url: string): Promise<number | null> {
  const cached = logoAspectCache.get(url);
  if (cached !== undefined) return cached;

  // First try the cheap path: let the browser decode the image and read
  // naturalWidth / naturalHeight. Works for all rasters and well-formed SVGs.
  const fromImage = await new Promise<number | null>((resolve) => {
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth > 1 && img.naturalHeight > 0) {
        resolve(img.naturalWidth / img.naturalHeight);
      } else {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
  if (fromImage) {
    logoAspectCache.set(url, fromImage);
    return fromImage;
  }

  // SVG fallback: many backend logos declare width/height="100%" which
  // collapses naturalWidth to 0. Parse the viewBox to recover the ratio.
  try {
    const res = await fetch(url);
    const text = await res.text();
    const viewBox = text.match(/viewBox=["']([^"']+)["']/i);
    if (viewBox) {
      const [, , w, h] = viewBox[1].split(/[\s,]+/).map(Number);
      if (w > 0 && h > 0) {
        const ratio = w / h;
        logoAspectCache.set(url, ratio);
        return ratio;
      }
    }
    const widthAttr = text.match(/<svg[^>]*\swidth=["']([\d.]+)/i);
    const heightAttr = text.match(/<svg[^>]*\sheight=["']([\d.]+)/i);
    if (widthAttr && heightAttr) {
      const w = parseFloat(widthAttr[1]);
      const h = parseFloat(heightAttr[1]);
      if (w > 0 && h > 0) {
        const ratio = w / h;
        logoAspectCache.set(url, ratio);
        return ratio;
      }
    }
  } catch {
    // ignore — chip will fall back to its CSS min-width.
  }
  return null;
}

watch(
  providerLogoUrl,
  (url) => {
    logoAspectRatio.value = null;
    if (!url || !import.meta.client) return;
    resolveLogoAspectRatio(url).then((ratio) => {
      if (ratio && providerLogoUrl.value === url) {
        logoAspectRatio.value = ratio;
      }
    });
  },
  { immediate: true }
);

// --- CTA -----------------------------------------------------------------

const externalUrl = computed(
  () =>
    props.estate?.referralLink?.link ||
    props.estate?.externalUrl ||
    props.estate?.provider?.url ||
    ""
);

const hasExternalTarget = computed(() => !!externalUrl.value);
const hasAnyCtaTarget = computed(
  () => hasExternalTarget.value || !!props.estate?.sanitizeName
);

const ctaLabel = computed(() => {
  if (isSold.value) return t("listing_card.fully_funded");
  if (hasExternalTarget.value) return t("listing_card.buy_tokens");
  return t("listing_card.invest");
});

const ariaLabel = computed(() => {
  const parts: string[] = [];
  if (props.estate?.name) parts.push(props.estate.name);
  if (apr.value !== null) parts.push(`APR ${aprDisplay.value}`);
  if (hasExternalTarget.value) parts.push(t("listing_card.buy_tokens"));
  return parts.join(" — ");
});

// --- GTM -----------------------------------------------------------------

const externalGoalKey = computed<keyof typeof gtagReachGoal>(() => {
  switch (props.source) {
    case "profile":
    case "favorites":
      return "profile__open_ref";
    case "suggestions":
      return "suggestions__open_ref";
    default:
      return "directory__open_ref";
  }
});

// --- Handlers ------------------------------------------------------------

const onInvest = () => {
  if (isSold.value) return;
  if (hasExternalTarget.value) {
    gtagReachGoal[externalGoalKey.value]?.({ value: externalUrl.value });
    redirectByProviderName({
      providerName: providerName.value,
      providerLink: props.estate?.provider?.url,
      utmLink:
        buildReferralRedirectUrl(props.estate?.referral) ||
        props.estate?.referralLink?.link,
      externalUrl: props.estate?.externalUrl,
      images: props.estate?.files,
      logoFile: props.estate?.provider?.logoFile?.url,
      source: externalGoalKey.value,
    });
    return;
  }
  if (props.estate?.sanitizeName) {
    window.open(detailsRoute.value, "_blank", "noopener");
  }
};
</script>

<style scoped lang="scss">
.listing-card-shell {
  // Owning the inline-size container here (rather than on .listing-card itself)
  // lets @container queries restyle the card element — a container cannot
  // query its own size to style itself, only its descendants.
  container-type: inline-size;
  container-name: listing-card;
  display: block;
  width: 100%;
  min-width: 0;
}

.listing-card {
  display: flex;
  flex-direction: column;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  overflow: hidden;
  min-width: 0;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: var(--shadow-card-hover);
  }

  &--sold-out {
    opacity: 0.65;
    &:hover {
      box-shadow: var(--shadow-card);
    }
  }

  // ---- Row variant: horizontal layout (e.g. /directory) ----
  &--row {
    flex-direction: row;
    align-items: stretch;

    @media (min-width: 768px) {
      min-height: 220px;
    }

    .listing-card__media {
      flex: 0 0 320px;
      aspect-ratio: unset;
      align-self: stretch;
    }
    .listing-card__body {
      flex: 1 1 auto;
      padding: 18px 22px;
      gap: 14px;
      min-width: 0;
    }

  }

  // ---- Tile variant: vertical layout (everywhere else) ----
  &--tile {
    min-width: 240px;

    .listing-card__media {
      aspect-ratio: 4 / 3;
    }
  }
}

// Adaptive metric-value font size — scales with card width via container query
// so numbers in any currency always fit without truncation.
@container listing-card (max-width: 320px) {
  .listing-card__metric-value {
    font-size: 17px;
  }
  .listing-card__metric {
    gap: 4px;
  }
}

@container listing-card (max-width: 280px) {
  .listing-card__metric-value {
    font-size: 15px;
    letter-spacing: -0.3px;
  }
}

@container listing-card (max-width: 260px) {
  .listing-card__metric-value {
    font-size: 13px;
    letter-spacing: -0.4px;
  }
  .listing-card__metrics {
    gap: 6px;
  }
}

// ---- Media ----

.listing-card__media {
  @include drop-button-styles;
  position: relative;
  display: block;
  width: 100%;
  background: var(--border-subtle);
  cursor: pointer;
  overflow: hidden;
  border: 0;
  padding: 0;
  text-decoration: none;
  color: inherit;

  // Absolute positioning prevents the <img> from imposing its natural aspect
  // ratio on the media container — portrait photos would otherwise inflate
  // the row-variant card height.
  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.listing-card__media-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 140px;
}

// ---- Sold-out pill ----

.listing-card__sold-pill {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 6px 12px;
  border-radius: var(--radius-pill);
  background: var(--text-primary);
  color: var(--surface);
  font-size: var(--font-micro);
  font-weight: var(--fw-bold);
  letter-spacing: 0.4px;
  text-transform: uppercase;
  white-space: nowrap;
}

// ---- Provider logo (bottom-left on photo, all variants/states) ----

.listing-card__provider-chip {
  position: absolute;
  bottom: 12px;
  left: 12px;
  // Default width before the JS-measured aspect ratio arrives — narrow enough
  // that the chip never looks oversized, wide enough for short wordmarks.
  width: 68px;
  height: 28px;
  max-width: calc(100% - 24px);
  border-radius: 6px;
  background-color: rgba(255, 255, 255, 0.9);
  background-repeat: no-repeat;
  background-position: center;
  background-size: auto 20px;
  box-shadow: var(--shadow-card);
  box-sizing: border-box;
}

// ---- Body ----

.listing-card__body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 18px 18px;
  min-width: 0;
}

.listing-card__title {
  @include drop-button-styles;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  cursor: pointer;
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  text-decoration: none;
  min-width: 0;
}

.listing-card__name {
  font-size: var(--font-body);
  font-weight: var(--fw-bold);
  line-height: var(--lh-heading);
  color: var(--text-primary);
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.listing-card__meta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-small);
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  :deep(svg) {
    flex-shrink: 0;
  }
}

// ---- Description (row variant only; fills the gap between title and metrics)
.listing-card__description {
  margin: 0;
  font-size: var(--font-small);
  color: var(--text-secondary);
  line-height: 1.5;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  // Prevent collapse when the description is a flex item (split layout) —
  // a -webkit-box can otherwise be shrunk below its content height.
  flex-shrink: 0;
}

// ---- Metrics ----

.listing-card__metrics {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: end;
  gap: 10px;
  padding: 14px 0;
  border-top: 1px solid var(--border-subtle);
  border-bottom: 1px solid var(--border-subtle);
}

// In the row variant the metrics sit just above the lavender actions panel,
// so the bottom border doubles up with the panel edge — drop it there.
.listing-card--row .listing-card__metrics {
  padding-bottom: 0;
  border-bottom: 0;
}

.listing-card__metric {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  min-width: 0;
}

.listing-card__metric-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: var(--fw-semibold);
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-secondary);
  max-width: 100%;
  min-width: 0;

  > span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  // base-tooltip wrapper renders inline; keep icon visually aligned with text baseline.
  :deep(.base-tooltip) {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    cursor: help;
    opacity: 0.6;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 1;
    }
  }
}

.listing-card__metric-value {
  font-size: var(--font-h3);
  font-weight: var(--fw-bold);
  line-height: 1.1;
  letter-spacing: -0.3px;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.listing-card__metric-sep {
  align-self: stretch;
  width: 1px;
  background: var(--border-subtle);
}

// ---- Actions panel (progress + trust + CTA + secondary) ----
// Lavender container only in --row; --tile keeps it flat on the card surface.

.listing-card__actions {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

// ---- Progress / supply ----

.listing-card__progress {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.listing-card__progress-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.listing-card__progress-pct {
  font-size: var(--font-small);
  font-weight: var(--fw-bold);
  color: var(--text-primary);
  white-space: nowrap;
}

.listing-card__progress-left {
  font-size: var(--font-micro);
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.listing-card__progress-track {
  width: 100%;
  height: 6px;
  background: var(--border-subtle);
  border-radius: var(--radius-pill);
  overflow: hidden;
}

.listing-card__progress-fill {
  height: 100%;
  background: var(--accent-primary);
  border-radius: var(--radius-pill);
  transition: width 0.4s ease;
}

// ---- Trust line (holders icon + count; LIQUID slot reserved) ----

.listing-card__trust {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-micro);
  color: var(--text-secondary);

  :deep(svg) {
    flex-shrink: 0;
  }
}

// ---- Details link (secondary, under CTA, centered, underlined) ----

.listing-card__details {
  align-self: center;
  font-size: var(--font-micro);
  color: var(--text-secondary);
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-color: rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition:
    color 0.2s ease,
    text-decoration-color 0.2s ease;

  &:hover {
    color: var(--text-primary);
    text-decoration-color: var(--text-primary);
  }
}

// ---- CTA ----

.listing-card__cta {
  @include drop-button-styles;
  @include ds-primary;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  padding: 0 20px;
  border-radius: var(--radius-pill);
  font-size: var(--font-small);
  font-weight: var(--fw-bold);
  text-align: center;
  transition:
    background-color 0.2s ease,
    transform 0.1s ease,
    opacity 0.2s ease;

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  @media (max-width: 767px) {
    height: auto;
    min-height: 52px;
    padding: 16px 20px;
  }
}

// Row variant @ desktop: title + metrics on left, actions cluster on right.
.listing-card--row {
  .listing-card__actions {
    padding: 16px 18px;
    background: var(--accent-soft);
    border-radius: var(--radius-md);
  }

  .listing-card__progress-track {
    background: rgba(255, 255, 255, 0.6);
  }

  .listing-card__body {
    @media (min-width: 768px) {
      display: grid;
      grid-template-columns: 1fr 240px;
      grid-template-rows: auto 1fr auto;
      column-gap: 28px;
      row-gap: 14px;
      align-items: start;

      .listing-card__title {
        grid-column: 1;
        grid-row: 1;
      }
      // Description gets the flex row — it fills whatever vertical space the
      // actions panel forces, with the line-clamp capping runaway text.
      .listing-card__description {
        grid-column: 1;
        grid-row: 2;
        align-self: start;
      }
      .listing-card__metrics {
        grid-column: 1;
        grid-row: 3;
        align-self: end;
      }
      .listing-card__actions {
        grid-column: 2;
        grid-row: 1 / span 3;
        align-self: stretch;
        justify-content: space-between;
        gap: 12px;
        height: 100%;

        // Keep CTA pinned to the bottom even on cards with fewer fields,
        // so all rows align CTA on the same horizontal line.
        .listing-card__cta {
          margin-top: auto;
        }
      }
    }
  }
}

// Card-width-driven fallback to vertical layout for the row variant.
// Triggered by the card's own inline size (container query on .listing-card-shell),
// so it kicks in both at narrow viewports and in the split map view where the
// listing column is half the viewport. Placed at the end of the stylesheet so it
// wins the cascade against the desktop grid block defined above.
@container listing-card (max-width: 720px) {
  .listing-card--row {
    flex-direction: column;
    min-height: unset;

    .listing-card__media {
      flex: 0 0 auto;
      aspect-ratio: 4 / 3;
      align-self: stretch;
      width: 100%;
    }

    .listing-card__body {
      display: flex;
      flex-direction: column;
      grid-template-columns: none;
      grid-template-rows: none;
      column-gap: 0;
      row-gap: 14px;
      align-items: stretch;
      padding: 16px 18px 18px;

      .listing-card__title,
      .listing-card__description,
      .listing-card__metrics,
      .listing-card__actions {
        grid-column: auto;
        grid-row: auto;
        align-self: stretch;
        height: auto;
      }
    }

    // Restore metrics bottom border (the horizontal-row rule drops it because
    // the lavender actions panel sits flush below it on desktop).
    .listing-card__metrics {
      padding-bottom: 14px;
      border-bottom: 1px solid var(--border-subtle);
    }
  }
}
</style>
